import { tokensAsNames } from '@/components/agent-chat/clu-mentions.util';

/**
 * The chats the runtime knows about before the lists do: the one the user
 * just started, when they last sent in each chat, and which real chat each
 * started one became. The sidebar's tabs and the Chats rail draw these on
 * top of what the server lists, so a chat shows the moment it is sent.
 */

/** A chat the user started that no list holds yet. */
export interface PendingChat {
  /** Its row's key for the whole session: `pending:<clientTurnId>`. */
  key: string;
  /** The real id, once the stream names it. */
  id: string | null;
  title: string;
  /** The agent whose workspace it was started in; null for Clu. */
  agentId: string | null;
  /** When it was sent, in ms. */
  at: number;
  /** Its send stopped before the id came: it goes at the next list read. */
  orphaned?: true;
}

export interface LocalChats {
  pending: PendingChat | null;
  /** Chat id to when the user last sent in it, in ms. */
  sentAt: Readonly<Record<string, number>>;
  /** Real id to the key it was started under, for the session. */
  aliases: Readonly<Record<string, string>>;
}

export const NO_LOCAL_CHATS: LocalChats = {
  pending: null,
  sentAt: {},
  aliases: {},
};

const PENDING_PREFIX = 'pending:';

/** The key a chat started by this turn is drawn under. */
export const pendingKey = (clientTurnId: string): string =>
  `${PENDING_PREFIX}${clientTurnId}`;

/** True for a started chat's key, which names no chat on the server. */
export const isPendingKey = (id: string): boolean =>
  id.startsWith(PENDING_PREFIX);

/** The server's cap on a chat's title. */
const TITLE_LIMIT = 120;

/**
 * A title from a message, as the server titles a new chat: each pick (a
 * list, an agent, a skill) as its name, whitespace collapsed and cut to 120
 * characters. Empty for a blank message.
 */
export function chatTitleFromMessage(message: string): string {
  const collapsed = tokensAsNames(message)
    .split(/\s+/)
    .filter(Boolean)
    .join(' ');
  return collapsed.length > TITLE_LIMIT
    ? `${collapsed.slice(0, TITLE_LIMIT - 1).trimEnd()}…`
    : collapsed;
}

/** A send that starts a chat: it shows at once, under its own key. */
export function startChat(
  local: LocalChats,
  chat: Pick<PendingChat, 'key' | 'title' | 'agentId' | 'at'>
): LocalChats {
  return { ...local, pending: { ...chat, id: null } };
}

/** A send in an existing chat moves it first, as of `at`. */
export function markSent(
  local: LocalChats,
  id: string,
  at: number
): LocalChats {
  return { ...local, sentAt: { ...local.sentAt, [id]: at } };
}

/** The stream named the chat `key` started: it is that chat from now on. */
export function resolveChat(
  local: LocalChats,
  key: string,
  id: string
): LocalChats {
  const { pending } = local;
  if (!pending || pending.key !== key || pending.id !== null) return local;
  return {
    pending: { ...pending, id },
    sentAt: { ...local.sentAt, [id]: pending.at },
    aliases: { ...local.aliases, [id]: key },
  };
}

/** The send failed before the chat had an id: there is no chat to show. */
export function dropChat(local: LocalChats, key: string): LocalChats {
  const { pending } = local;
  if (!pending || pending.key !== key || pending.id !== null) return local;
  return { ...local, pending: null };
}

/**
 * The send stopped before the chat had an id (New chat, another chat opened,
 * or Stop): the server may still have made it, so the row waits for the
 * next list read. Any key when none is given.
 */
export function orphanChat(local: LocalChats, key?: string): LocalChats {
  const { pending } = local;
  if (!pending || pending.id !== null || pending.orphaned) return local;
  if (key !== undefined && pending.key !== key) return local;
  return { ...local, pending: { ...pending, orphaned: true } };
}

/**
 * A read of a list: Clu's (`agentIds` left out), or the chats of the agents
 * `agentIds`. It settles a started chat of its own kind only, since Clu's
 * list never holds an agent's chat. One the read holds leaves the overlay,
 * and so does one whose send stopped before it had an id: the read brings
 * in the real chat if the server made one. An agent's chat whose agent the
 * read leaves out stays while it has an id, since no list will show it.
 */
export function settleChats(
  local: LocalChats,
  listed: ReadonlySet<string>,
  agentIds?: ReadonlySet<string>
): LocalChats {
  const { pending } = local;
  if (!pending) return local;
  if ((pending.agentId === null) !== (agentIds === undefined)) return local;
  if (pending.orphaned || (pending.id !== null && listed.has(pending.id)))
    return { ...local, pending: null };
  return local;
}

/** A chat deleted: it is not drawn from the overlay either. */
export function forgetChat(local: LocalChats, id: string): LocalChats {
  if (local.pending?.id !== id && local.sentAt[id] === undefined) return local;
  const sentAt = { ...local.sentAt };
  delete sentAt[id];
  return {
    ...local,
    pending: local.pending?.id === id ? null : local.pending,
    sentAt,
  };
}

/** The started chat the runtime has open, still waiting for its id. */
export function livePendingChat(local: LocalChats): PendingChat | null {
  const { pending } = local;
  return pending && pending.id === null && !pending.orphaned ? pending : null;
}
